import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Text,
  VStack,
  HStack,
  useToast,
  Badge,
  Spinner,
  Progress,
  Icon,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  List,
  ListItem,
  ListIcon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Flex,
} from "@chakra-ui/react";
import { FaAmazon, FaSync, FaCheckCircle, FaTimesCircle, FaExclamationTriangle, FaTimes, FaTrash } from "react-icons/fa";
import { useAuth } from "../../context/AuthContext";
import { useTranslation } from "react-i18next";
import Card from "../Card/Card";
import CardHeader from "../Card/CardHeader";
import CardBody from "../Card/CardBody";

interface SyncStatus {
  status: 'idle' | 'pending' | 'processing' | 'completed' | 'failed';
  message?: string;
  progress?: number;
  totalAsins?: number;
  processedAsins?: number;
  error?: string;
}

const SYNC_JOB_STORAGE_KEY = 'amazon_sync_job_id';

const AmazonIntegration = () => {
  const { currentUser, getToken } = useAuth();
  const { t } = useTranslation();
  const toast = useToast();
  const { isOpen: isOpenCancel, onOpen: onOpenCancel, onClose: onCloseCancel } = useDisclosure();
  const { isOpen: isOpenRevoke, onOpen: onOpenRevoke, onClose: onCloseRevoke } = useDisclosure();

  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [loadingAuth, setLoadingAuth] = useState<boolean>(true);
  const [syncStatus, setSyncStatus] = useState<SyncStatus>({ status: 'idle' });
  const [jobId, setJobId] = useState<string | null>(() => {
    // Récupérer le jobId depuis localStorage au montage
    return localStorage.getItem(SYNC_JOB_STORAGE_KEY);
  });

  // Vérifier le statut de connexion au chargement
  useEffect(() => {
    checkAuthStatus();
  }, [currentUser]);

  // Restaurer l'état de sync si un job est en cours
  useEffect(() => {
    if (jobId) {
      setSyncStatus({ status: 'processing', message: 'Reprise de la synchronisation...' });
    }
  }, []);

  // Polling pour le statut de synchronisation
  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (jobId && (syncStatus.status === 'pending' || syncStatus.status === 'processing')) {
      interval = setInterval(async () => {
        await checkSyncStatus(jobId);
      }, 3000); // Poll toutes les 3s
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [jobId, syncStatus.status]);

  const checkAuthStatus = async () => {
    try {
      setLoadingAuth(true);
      const token = await getToken();
      const response = await fetch("/.netlify/functions/amazonAuthStatus", {
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setIsConnected(data.isConnected);
      }
    } catch (error) {
      console.error("Auth check failed", error);
    } finally {
      setLoadingAuth(false);
    }
  };

  const handleConnect = async () => {
    try {
      const token = await getToken();
      const response = await fetch("/.netlify/functions/amazonGetAuthUrl", {
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      if (response.ok) {
        const { url } = await response.json();
        window.location.href = url; // Redirection vers Amazon
      } else {
        toast({
          title: "Erreur",
          description: "Impossible d'obtenir l'URL de connexion",
          status: "error",
        });
      }
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Une erreur est survenue",
        status: "error",
      });
    }
  };

  const handleSync = async () => {
    try {
      setSyncStatus({ status: 'pending', message: t('syncInProgress') });
      const token = await getToken();
      
      const response = await fetch("/.netlify/functions/amazonSyncInventory", {
        method: "POST",
        headers: { 
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}` 
        },
        body: JSON.stringify({ userId: currentUser?.uid })
      });

      if (response.ok) {
        const data = await response.json();
        setJobId(data.jobId);
        localStorage.setItem(SYNC_JOB_STORAGE_KEY, data.jobId); // Persister le jobId
        setSyncStatus({ status: 'processing', message: t('syncInProgress') });
        
        toast({
          title: t('syncStarted'),
          status: "info",
        });
      } else {
        throw new Error("Failed to start sync");
      }
    } catch (error) {
      setSyncStatus({ status: 'failed', error: t('syncFailed') });
      localStorage.removeItem(SYNC_JOB_STORAGE_KEY);
      toast({
        title: t('error'),
        description: t('syncFailed'),
        status: "error",
      });
    }
  };

  const handleCancelSync = () => {
      localStorage.removeItem(SYNC_JOB_STORAGE_KEY);
      setJobId(null);
      setSyncStatus({ status: 'idle' });
      onCloseCancel();
      toast({
          title: t('syncCancelled'),
          description: t('syncCancelledDesc'),
          status: "info",
      });
  };

  const handleRevoke = async () => {
    try {
        const token = await getToken();
        const response = await fetch("/.netlify/functions/amazonRevokeAuth", {
            method: "DELETE",
            headers: { "Authorization": `Bearer ${token}` }
        });
        
        if (response.ok) {
            setIsConnected(false);
            setSyncStatus({ status: 'idle' });
            localStorage.removeItem(SYNC_JOB_STORAGE_KEY);
            setJobId(null);
            
            toast({
                title: t('accessRevoked'),
                description: t('accessRevokedDesc'),
                status: "success",
            });
        } else {
            throw new Error("Revoke failed");
        }
    } catch (error) {
         toast({
            title: t('error'),
            status: "error",
        });
    } finally {
        onCloseRevoke();
    }
  };

  const checkSyncStatus = async (currentJobId: string) => {
    try {
      const token = await getToken();
      const response = await fetch(`/.netlify/functions/amazonCheckSyncStatus?jobId=${currentJobId}`, {
        headers: { "Authorization": `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setSyncStatus(data); // { status, progress, message, ... }

        if (data.status === 'completed') {
          toast({
            title: "Synchronisation terminée !",
            description: `${data.processedAsins || 0} produits récupérés.`,
            status: "success",
            duration: 5000,
          });
          setJobId(null);
          localStorage.removeItem(SYNC_JOB_STORAGE_KEY); // Nettoyer le storage
        } else if (data.status === 'failed') {
          toast({
            title: "Échec de la synchronisation",
            description: data.error,
            status: "error",
          });
          setJobId(null);
          localStorage.removeItem(SYNC_JOB_STORAGE_KEY); // Nettoyer le storage
        }
      }
    } catch (error) {
      console.error("Polling error", error);
    }
  };

  return (
    <Card p="20px" my={{ sm: "24px", xl: "0px" }}>
      <CardHeader>
        <Text fontSize="lg" color="white" fontWeight="bold">
          Intégration Amazon SP-API
        </Text>
      </CardHeader>
      <CardBody>
        <VStack spacing={6} align="stretch" w="100%">
          
          {/* Status Section */}
          <Box 
            p={6} 
            borderRadius="20px" 
            bg="linear-gradient(127.09deg, rgba(6, 11, 40, 0.94) 19.41%, rgba(10, 14, 35, 0.49) 76.65%)"
            border="1px solid" 
            borderColor="gray.700"
          >
            <HStack justify="space-between">
              <HStack spacing={4}>
                <Flex 
                    align="center" 
                    justify="center" 
                    borderRadius="12px" 
                    bg="orange.400" 
                    h="50px" 
                    w="50px"
                >
                    <Icon as={FaAmazon} boxSize={6} color="white" />
                </Flex>
                <VStack align="start" spacing={0}>
                    <Text color="white" fontSize="lg" fontWeight="bold">{t('amazonSellerAccount')}</Text>
                    <Text color="gray.400" fontSize="sm">
                        {isConnected ? t('connected') : t('disconnected')}
                    </Text>
                </VStack>
              </HStack>
              
              {loadingAuth ? (
                <Spinner size="sm" color="white" />
              ) : isConnected ? (
                <HStack>
                    <Badge 
                        colorScheme="green" 
                        variant="solid" 
                        px={3} 
                        py={1} 
                        borderRadius="full" 
                        display="flex" 
                        alignItems="center" 
                        gap={2}
                        fontSize="xs"
                    >
                        <Icon as={FaCheckCircle} /> {t('connected')}
                    </Badge>
                    <Button 
                        size="sm" 
                        colorScheme="red" 
                        variant="outline" 
                        onClick={onOpenRevoke}
                        title={t('revokeAccess')}
                        _hover={{ bg: "red.500", color: "white" }}
                    >
                        <Icon as={FaTrash} />
                    </Button>
                </HStack>
              ) : (
                <Badge 
                    colorScheme="red" 
                    variant="solid" 
                    px={3} 
                    py={1} 
                    borderRadius="full" 
                    display="flex" 
                    alignItems="center" 
                    gap={2}
                    fontSize="xs"
                >
                  <Icon as={FaTimesCircle} /> {t('disconnected')}
                </Badge>
              )}
            </HStack>

            {!isConnected && !loadingAuth && (
              <Button 
                mt={6} 
                colorScheme="orange" 
                w="100%" 
                h="50px"
                onClick={handleConnect}
                leftIcon={<FaAmazon />}
                fontSize="md"
                fontWeight="bold"
              >
                {t('connectSellerCentral')}
              </Button>
            )}
          </Box>

          {/* Sync Section (Visible only if connected) */}
          {isConnected && (
            <Box 
                p={6} 
                borderRadius="20px" 
                bg="linear-gradient(127.09deg, rgba(6, 11, 40, 0.94) 19.41%, rgba(10, 14, 35, 0.49) 76.65%)"
                border="1px solid" 
                borderColor="gray.700"
            >
              <HStack justify="space-between" mb={6}>
                <HStack spacing={4}>
                    <Flex 
                        align="center" 
                        justify="center" 
                        borderRadius="12px" 
                        bg="teal.400" 
                        h="50px" 
                        w="50px"
                    >
                        <Icon as={FaSync} boxSize={5} color="white" />
                    </Flex>
                    <VStack align="start" spacing={0}>
                        <Text color="white" fontSize="lg" fontWeight="bold">{t('syncInventory')}</Text>
                        <Text color="gray.400" fontSize="sm">
                            Mettez à jour vos produits
                        </Text>
                    </VStack>
                </HStack>

                {syncStatus.status !== 'processing' && syncStatus.status !== 'pending' && (
                    <Button 
                        colorScheme="teal" 
                        onClick={handleSync}
                        leftIcon={<FaSync />}
                        variant="solid"
                        h="40px"
                    >
                        {t('syncNow')}
                    </Button>
                )}
              </HStack>

              {/* Progress Bar */}
              {(syncStatus.status === 'processing' || syncStatus.status === 'pending') && (
                <Box>
                    <HStack justify="space-between" mb={2}>
                        <Text fontSize="sm" color="gray.400" fontWeight="medium">{syncStatus.message || t('syncInProgress')}</Text>
                        <Button size="xs" colorScheme="red" variant="ghost" onClick={onOpenCancel} leftIcon={<FaTimes />}>
                            {t('cancelSync')}
                        </Button>
                    </HStack>
                    <Progress hasStripe isIndeterminate value={100} colorScheme="teal" borderRadius="full" size="sm" />
                </Box>
              )}

              {/* Success State */}
              {syncStatus.status === 'completed' && (
                <Alert status="success" borderRadius="12px" mt={4}>
                    <AlertIcon />
                    <Box>
                        <AlertTitle fontSize="sm">{t('syncSuccess')}</AlertTitle>
                        <AlertDescription fontSize="xs">
                            {t('productsUpdated', { count: syncStatus.processedAsins || 0 })}
                        </AlertDescription>
                    </Box>
                </Alert>
              )}

              {/* Error State */}
              {syncStatus.status === 'failed' && (
                <Alert status="error" borderRadius="12px" mt={4}>
                    <AlertIcon />
                    <AlertDescription fontSize="sm">{syncStatus.error || t('unexpectedError')}</AlertDescription>
                </Alert>
              )}
            </Box>
          )}

          {/* Info Section */}
          <Box 
            p={6} 
            borderRadius="20px" 
            bg="gray.800" 
            border="1px solid" 
            borderColor="gray.700"
          >
            <Text fontSize="md" color="white" fontWeight="bold" mb={4}>{t('whyConnectAccount')}</Text>
            <List spacing={3}>
                <ListItem fontSize="sm" color="gray.400" display="flex" alignItems="center">
                    <ListIcon as={FaCheckCircle} color="green.400" boxSize={4} />
                    {t('whyConnectReason1')}
                </ListItem>
                <ListItem fontSize="sm" color="gray.400" display="flex" alignItems="center">
                    <ListIcon as={FaCheckCircle} color="green.400" boxSize={4} />
                    {t('whyConnectReason2')}
                </ListItem>
                <ListItem fontSize="sm" color="gray.400" display="flex" alignItems="center">
                    <ListIcon as={FaCheckCircle} color="green.400" boxSize={4} />
                    {t('whyConnectReason3')}
                </ListItem>
            </List>
          </Box>

        </VStack>
      </CardBody>

      {/* Modal Confirmation Annulation Sync */}
      <Modal isOpen={isOpenCancel} onClose={onCloseCancel}>
        <ModalOverlay />
        <ModalContent bg="gray.800" color="white">
            <ModalHeader>{t('cancelSyncConfirmTitle')}</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
                <Text color="gray.300">
                    {t('cancelSyncConfirmDesc')}
                </Text>
            </ModalBody>

            <ModalFooter>
                <Button variant="ghost" mr={3} onClick={onCloseCancel} color="gray.400">
                    {t('back')}
                </Button>
                <Button colorScheme="red" onClick={handleCancelSync}>
                    {t('stopTracking')}
                </Button>
            </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Modal Confirmation Revocation */}
      <Modal isOpen={isOpenRevoke} onClose={onCloseRevoke}>
        <ModalOverlay />
        <ModalContent bg="gray.800" color="white">
            <ModalHeader>{t('revokeAccessConfirmTitle')}</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
                <Text color="gray.300">
                    {t('revokeAccessConfirmDesc')}
                </Text>
            </ModalBody>

            <ModalFooter>
                <Button variant="ghost" mr={3} onClick={onCloseRevoke} color="gray.400">
                    {t('back')}
                </Button>
                <Button colorScheme="red" onClick={handleRevoke}>
                    {t('disconnect')}
                </Button>
            </ModalFooter>
        </ModalContent>
      </Modal>
    </Card>
  );
};

export default AmazonIntegration;
